import {
  IHIValidationRequest,
  InformationalResponse,
  PatientFullProfile,
  PatientProfileUpdateRequest,
  Warning,
} from '@/lib/apis/types'
import {
  IHIVerificationHistoryUrl,
  VerifyPatientIHIUrl,
} from '@/lib/apis/routes'
import { MutationKeys } from '@/lib/apis/service'
import WebRequest from '@/lib/apis/WebRequest'
import { ApiClient } from '@/lib/apis/clients/ApiClient'
import qs from 'qs'
import { GenericUploadSupportingDocuments } from '@/lib/apis/generic'

export namespace GetPatients {
  type Params = {
    index: number
    name: string
    email: string
    dob: string
  }

  export function api({ index, name, email, dob }: Params): WebRequest {
    const url = '/patients'
    const params: { [key: string]: any } = {}

    if (index) params['index'] = index
    if (name) params['name'] = name
    if (email) params['email'] = email
    if (dob) params['dob'] = dob

    const search = qs.stringify(params)
    const finalURL = search.length > 0 ? `${url}?${search}` : url
    return WebRequest.GET(finalURL)
  }
}

export namespace GetPatientByReference {
  type Params = {
    reference: string
  }

  export function api(
    client: ApiClient,
    { reference }: Params,
  ): Promise<PatientFullProfile> {
    const request = WebRequest.GET(`/patients/${reference}`)
    return client.execute(request)
  }
}

export namespace UpdatePatientProfile {
  type Params = {
    reference: string
    request: PatientProfileUpdateRequest
  }

  export function api({ reference, request }: Params): WebRequest {
    return WebRequest.POST(`/patients/${reference}`).body(request)
  }

  export function mutation(client: ApiClient) {
    return {
      mutationFn: (params: Params) =>
        client.execute<InformationalResponse>(api(params)),
      mutationKey: MutationKeys.UpdatePatientProfile(),
    }
  }
}

export namespace VerifyPatientIHI {
  type Params = {
    reference: string
    data: IHIValidationRequest
  }

  function api(params: Params): WebRequest {
    return WebRequest.POST(VerifyPatientIHIUrl(params.reference)).body(
      params.data,
    )
  }

  export function mutation(client: ApiClient, reference: string) {
    return {
      mutationFn: (params: Omit<Params, 'reference'>) =>
        client.execute<Array<Warning>>(api({ ...params, reference })),
      mutationKey: MutationKeys.VerifyPatientIHI(reference),
    }
  }
}

export namespace GetIHIVerificationHistory {
  type Params = {
    reference: string
  }

  export function api({ reference }: Params): WebRequest {
    return WebRequest.GET(IHIVerificationHistoryUrl(reference))
  }
}

export namespace UploadSupportingDocumentsForConsultation {
  export function mutation(client: ApiClient) {
    return GenericUploadSupportingDocuments.mutation(
      client,
      '/patient-documents/supporting-documents',
      MutationKeys.UploadReferralSupportingDocuments(),
    )
  }
}

export namespace UploadQuizPhotos {
  export function mutation(client: ApiClient) {
    return GenericUploadSupportingDocuments.mutationWithResponse(
      client,
      '/patient-documents/quiz-photos',
      MutationKeys.UploadQuizPhotos(),
    )
  }
}

export namespace UploadQuizFiles {
  export function mutation(client: ApiClient) {
    return GenericUploadSupportingDocuments.mutationWithResponse(
      client,
      '/patient-documents/quiz-files',
      MutationKeys.UploadQuizFiles(),
    )
  }
}

export namespace GetQuizPhotoPreviewUrl {
  type Params = {
    id: string
  }

  export type QuizPhotoPreviewResponse = {
    url: string
  }

  export async function api(
    client: ApiClient,
    params: Params,
  ): Promise<QuizPhotoPreviewResponse> {
    const request = WebRequest.POST('/patient-documents/quiz-photos/preview').body(
      params,
    )
    return client.execute<QuizPhotoPreviewResponse>(request)
  }
}

export namespace GetQuizFilePreviewUrl {
  type Params = {
    id: string
  }

  export type QuizFilePreviewResponse = {
    url: string
  }

  export async function api(
    client: ApiClient,
    params: Params,
  ): Promise<QuizFilePreviewResponse> {
    const request = WebRequest.POST('/patient-documents/quiz-files/preview').body(
      params,
    )
    return client.execute<QuizFilePreviewResponse>(request)
  }
}
